import React from "react";
import { useScrollReveal } from "./useAnimations";
import "./FeaturesGrid.scss";

/* ---- SVG Icon Components ---- */
const IconTarget = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" stroke="#DB4435" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="6" stroke="#DB4435" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="2" fill="#DB4435" />
    <path
      d="M12 2v4M12 18v4M2 12h4M18 12h4"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconShield = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 3L4 7v5c0 4.5 3.4 8.7 8 10 4.6-1.3 8-5.5 8-10V7l-8-4z"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconBolt = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13 2L4.5 13H12l-1 9 8.5-11H12l1-9z"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconPrice = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2"
      y="5"
      width="20"
      height="14"
      rx="2"
      stroke="#DB4435"
      strokeWidth="1.5"
    />
    <path d="M2 10h20" stroke="#DB4435" strokeWidth="1.5" />
    <circle cx="17" cy="15" r="1.5" stroke="#DB4435" strokeWidth="1" />
    <circle cx="14" cy="15" r="1.5" stroke="#DB4435" strokeWidth="1" />
  </svg>
);

const IconRocket = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C12 2 7 7.5 7 13c0 2.8 2.2 5 5 5s5-2.2 5-5c0-5.5-5-11-5-11z"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="13" r="2" stroke="#DB4435" strokeWidth="1.5" />
    <path
      d="M5 18l-2 3M19 18l2 3"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconCloud = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 18h8c2.8 0 5-2.2 5-5 0-2.5-1.8-4.5-4.2-4.9A6 6 0 005 11c0 .4 0 .7.1 1A4 4 0 008 18z"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 13v5M10 16l2 2 2-2"
      stroke="#DB4435"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const iconMap = {
  target: IconTarget,
  shield: IconShield,
  bolt: IconBolt,
  price: IconPrice,
  rocket: IconRocket,
  cloud: IconCloud,
};

const features = [
  {
    icon: "target",
    title: "Senior-Led Delivery",
    desc:
      "Every engagement is led by senior architects and consultants \u2014 no team handoffs, no escalation layers.",
  },
  {
    icon: "shield",
    title: "24/7 Governance-Grade Security",
    desc:
      "ISO 27001, SOC 2 and GDPR-aligned delivery as standard across every project.",
  },
  {
    icon: "bolt",
    title: "AI-Accelerated Execution",
    desc:
      "AI compresses timelines by 40\u201360% without compromising quality or architectural oversight.",
  },
  {
    icon: "price",
    title: "Zero Guesswork Pricing",
    desc:
      "Transparent flat-rate or hourly billing with full practice rates for all of your budgets.",
  },
  {
    icon: "rocket",
    title: "Weeks to First Outcome",
    desc:
      "Compressed same-day resolution with our 24/7 emergency response team for critical issues.",
  },
  {
    icon: "cloud",
    title: "Always Scaling Architecture",
    desc:
      "We build systems to handle 10\u00D7 growth without any major re-engineering.",
  },
];

const FeatureCard = ({ feature, index }) => {
  const [ref, vis] = useScrollReveal({ threshold: 0.1 });
  const Icon = iconMap[feature.icon];

  return (
    <div
      ref={ref}
      className={`feat__card${vis ? " is-visible" : ""}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="feat__card-icon-wrap">
        <Icon />
      </div>
      <h3 className="feat__card-title">{feature.title}</h3>
      <p className="feat__card-desc">{feature.desc}</p>
      <div className="feat__card-line" />
    </div>
  );
};

const FeaturesGrid = () => {
  const [hRef, hVis] = useScrollReveal();
  const [imgRef, imgVis] = useScrollReveal({ threshold: 0.15 });

  return (
    <section className="feat">
      <div className="feat__inner">
        <div ref={hRef} className="feat__header">
          <span className={`feat__label${hVis ? " is-visible" : ""}`}>
            Why Choose Us
          </span>
          <h2 className={`feat__title${hVis ? " is-visible" : ""}`}>
            Expertise that makes
            <br />
            the difference
          </h2>
        </div>

        <div className="feat__grid">
          {features.map((f, i) => (
            <FeatureCard key={i} feature={f} index={i} />
          ))}
        </div>

        {/* Stock image banner */}
        <div
          ref={imgRef}
          className={`feat__image-row${imgVis ? " is-visible" : ""}`}
        >
          <div className="feat__image-card">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=600&q=80"
              alt="Team collaboration in a modern office"
              loading="lazy"
            />
            <span className="feat__image-caption">
              Senior-led teams delivering at pace
            </span>
          </div>
          <div className="feat__image-card">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=600&q=80"
              alt="Software engineers working on code"
              loading="lazy"
            />
            <span className="feat__image-caption">
              AI-accelerated engineering delivery
            </span>
          </div>
          <div className="feat__image-card">
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80"
              alt="Data analytics dashboard"
              loading="lazy"
            />
            <span className="feat__image-caption">
              Governance-grade oversight built in
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
